import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { take, map } from 'rxjs/operators';

import type { Entity, TreeEntity } from '../models/entity.model';
import { EntityService } from './entity.service';
import { EntitiesPaged } from '../models/entities-paged.model';
import { EntityType } from '../models/entity-type.model';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class ProjectEntityService {
  constructor(private entityService: EntityService, private translateService: TranslateService, private httpClient: HttpClient) {}

  queryProjectEntities(searchPhrase: string, page: number, pageSize: number): Observable<EntitiesPaged> {
    return this.entityService.queryEntities(searchPhrase, page, pageSize).pipe(
      take(1),
      map(response => {
        return response;
      })
    );
  }

  getProjectEntity(id: string): Observable<Entity[]> {
    return this.entityService.getEntityTree(id);
  }

  sortEntityChildren(entity: TreeEntity, entityTypes: Record<string, EntityType>, level: number) {
    // Root level sorted by date, the next level by position id (currently position is reflected in the name of entity), and all others alphabetically
    if (entity.Children !== undefined && entity.Children.length > 0) {
      entity.Children.map(child => {
        return this.sortEntityChildren(child, entityTypes, level + 1);
      });
      if (entity.Children.length > 1) {
        if (level === 0 || level === 1) {
          entity.Children.sort((a: TreeEntity, b: TreeEntity) => {
            // sort by Number property if it exists, otherwise by the date created
            const aNumberProperty = a.Properties['Number'];
            const bNumberProperty = b.Properties['Number'];
            if (aNumberProperty !== undefined && bNumberProperty !== undefined) {
              return (aNumberProperty.Value as number) - (bNumberProperty.Value as number);
            }

            return new Date(b.DateCreated).getTime() - new Date(a.DateCreated).getTime();
          });
        } else {
          entity.Children.sort((a: Entity, b: Entity) => {
            const aType = entityTypes[a.EntityTypeKey];
            const bType = entityTypes[b.EntityTypeKey];

            if (!aType || !bType) {
              return 0;
            }

            const aName = (aType.NameId && this.translateService.instant(aType.NameId)) || aType.Key;
            const bName = (bType.NameId && this.translateService.instant(bType.NameId)) || bType.Key;
            if (aName < bName) {
              return -1;
            }
            if (aName > bName) {
              return 1;
            }
            return 0;
          });
        }
      }
    }

    return entity;
  }

  exportToPyramid(id: string): Observable<string> {
    return this.httpClient.post<string>(`${environment.apiUrl}/export/pyramid/${id}`, {}).pipe(take(1));
  }
}
